/**
 * SourceViewer - Universal implementation
 * Uses WebView for webpages, PDF renderer for PDFs
 */
import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import Icon from '~/components/Icon'
import type { SourceViewerProps, ViewerState } from '~/components/source-viewer/types'
import { Button } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import designTokens from '~/lib/designTokens'
import { cn, isFileUrl, isWebUrl } from '~/lib/utils'
import PdfViewer from './PdfViewer'
import WebView from './WebView'

function useContentHandlers(
  source: SourceViewerProps['source'],
  type: SourceViewerProps['type'],
  onLoad?: () => void,
  onError?: (error: string) => void
) {
  const { t } = useTranslations('sourceViewer')
  const [state, setState] = useState<ViewerState>({
    status: 'ready',
  })

  const prevSource = useRef<SourceViewerProps['source'] | null>(null)

  useEffect(() => {
    if (source && (!prevSource.current || source !== prevSource.current)) {
      prevSource.current = source
      const validationFn = type === 'webpage' ? isWebUrl : isFileUrl

      if (!validationFn(source)) {
        setState({ status: 'error', message: t(($) => $.error.invalidUrl) })
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
        onError?.(t(($) => $.error.invalidUrl))
        return
      }

      setState({ status: 'loading' })
    } else if (!source) {
      setState({ status: 'ready' })
    }
  }, [source, type, state.status, onError, t])

  const handleContentLoad = useCallback(() => {
    setState({ status: 'ready' })
    onLoad?.()
  }, [onLoad])

  const handleContentError = useCallback(
    (error: string) => {
      setState({ status: 'error', message: error })
      onError?.(error)
    },
    [onError]
  )

  return { handleContentLoad, handleContentError, state, setState }
}

function ErrorView({
  message,
  onRetry,
  className,
}: {
  message: string
  onRetry?: () => void
  className?: string
}) {
  const { t } = useTranslations('sourceViewer')
  return (
    <View className={cn('flex-1 items-center justify-center gap-4 p-6', className)}>
      <Icon name="alert-circle" size={48} className="text-destructive" />
      <Text className="text-center font-semibold text-lg">{t(($) => $.error.title)}</Text>
      <Text className="text-center text-secondary">{message}</Text>
      {onRetry && (
        <Button onPress={onRetry} variant="outline" size="sm">
          {t(($) => $.error.retry)}
        </Button>
      )}
    </View>
  )
}

function LoadingView({
  message,
  className,
  disabled = false,
}: {
  message: string
  className?: string
  disabled?: boolean
}) {
  const { t } = useTranslations('sourceViewer')
  return (
    <View className={cn('flex-1 items-center justify-center p-4', className)}>
      <ActivityIndicator size="large" color={designTokens.foreground} />
      <Text className="mt-2 text-center text-secondary">
        {disabled ? t(($) => $.loading.message) : message}
      </Text>
    </View>
  )
}

function EmptyView({ message, className }: { message: string; className?: string }) {
  return (
    <View className={cn('flex-1 items-center justify-center p-4', className)}>
      <Icon name="globe-outline" size={48} className="text-secondary" />
      <Text className="mt-2 text-center text-secondary">{message}</Text>
    </View>
  )
}

export default function SourceViewer({
  source,
  type,
  onLoad,
  onError,
  className,
  errorMessage,
  placeholder,
}: SourceViewerProps) {
  const { t } = useTranslations('sourceViewer')
  const { handleContentLoad, handleContentError, state } = useContentHandlers(
    source,
    type,
    onLoad,
    onError
  )

  if (state.status === 'error') {
    return <ErrorView message={errorMessage || state.message} className={className} />
  }

  const viewerClassName = cn('h-full w-full flex-1', state.status === 'loading' && 'hidden')

  const viewer =
    type === 'webpage' ? (
      <WebView
        source={source}
        type={type}
        onLoad={handleContentLoad}
        onError={() => handleContentError(t(($) => $.error.webpageLoadFailed))}
        className={viewerClassName}
      />
    ) : type === 'pdf' ? (
      <PdfViewer
        source={source}
        onLoad={handleContentLoad}
        onError={() => handleContentError(t(($) => $.error.pdfLoadFailed))}
        className={viewerClassName}
      />
    ) : null

  return (
    <View className={cn('relative h-full w-full flex-1', className)}>
      {viewer ?? (
        <ErrorView message={t(($) => $.error.unknownContent)} className="absolute inset-0" />
      )}
      {state.status === 'loading' && (
        <LoadingView message={t(($) => $.loading.message)} className="absolute inset-0" />
      )}
      {!source && state.status === 'ready' && (
        <EmptyView
          message={
            placeholder ?? (type === 'webpage' ? t(($) => $.empty.webpage) : t(($) => $.empty.pdf))
          }
          className="absolute inset-0"
        />
      )}
    </View>
  )
}
